import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Formik, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { Col, FormGroup, Form, Row, Button, Alert } from "react-bootstrap";
import { formikArrayInput, formikInput } from "../../../utils/utils";
import {
  Invoice2,
  InvoiceApprovalStatus,
  LineItem2,
  TransactionLine,
} from "src/docuclipper/DocuclipperTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  LoadingButton,
  TrackedButtonWithTooltip,
} from "src/views/Docuclipper/TrackedButton";
import {
  handleImportToQbo,
  setDuplicateError,
  updateInvoiceField,
  updateInvoiceLineItem,
} from "src/redux/reducers/InvoiceManager";
import { ReduxState } from "src/redux";
import { fetchIntegrations } from "src/redux/reducers/Integration";
import {
  fetchXeroContacts,
  createAndAddXeroContact,
} from "src/redux/reducers/XeroContact";
import {
  fetchXeroAccounts,
  createAndAddXeroAccount,
} from "src/redux/reducers/XeroAccount";
import {
  fetchXeroItems,
  createAndAddXeroItem,
} from "src/redux/reducers/XeroItem";
import {
  searchXeroContacts,
  searchXeroAccounts,
  searchXeroItems,
} from "src/docuclipper/api";
import { fetchXeroTaxRates } from "src/redux/reducers/XeroTaxRate";
import { FormikQboSelect } from "./QboSelect";
import CategorizationBadge from "./CategorizationBadge";
import { ApprovalHistory } from "./ApprovalHistory";
import TagRegexesInvoice from "../Analyze/Categorize/TagRegexesInvoice";
import { createAlert } from "src/redux/reducers/Alerts";
import { SUPPORTED_SCOPES } from "src/redux/reducers/TagLevelIntegration";
import {
  fetchApprovalStatus,
  approveInvoice,
  rejectInvoice,
  resetState,
} from "src/redux/reducers/InvoiceApprovalStatus";
import {
  InvoiceFormProps,
  defaultInvoiceLine,
  createBaseValidationSchema,
  createXeroLineItemsValidation,
  createXeroCustomerValidation,
  createXeroVendorValidation,
  createXeroAccountValidation,
  pulseAnimationStyles,
  getOptionLabelById,
  createTaxLineItem,
} from "./invoiceFormUtils";
import {
  checkInvoiceConsistency,
  formatDelta,
} from "./invoiceConsistency";
import InvoiceFieldWarning from "./InvoiceFieldWarning";

const InvoiceXero: React.FC<InvoiceFormProps> = ({
  invoice,
  getMarginTop,
  tl,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { steps } = useSelector((state: ReduxState) => state.OnboardingSteps);
  const animate = steps.filter(
    (s) => s.name === "DOWNLOAD_DATA" && !s.completed
  );

  const {
    importToQboLoading,
    duplicateError,
    approvalStatus,
    linkedIntegration,
    linkedIntegrationDetails,
    // Xero state
    xeroContacts,
    xeroAccounts,
    xeroItems,
    xeroTaxRates,
    xeroContactsLoading,
    xeroAccountsLoading,
    xeroItemsLoading,
    xeroTaxRatesLoading,
  } = useSelector((state: ReduxState) => ({
    importToQboLoading: state.InvoiceManager.importToQboLoading,
    duplicateError: state.InvoiceManager.duplicateError,
    approvalStatus: state.InvoiceApprovalStatus,
    linkedIntegration: state.TagLevelIntegration.linkedIntegration,
    linkedIntegrationDetails:
      state.TagLevelIntegration.linkedIntegrationDetails,
    // Xero state
    xeroContacts: state.XeroContact.xeroContacts,
    xeroAccounts: state.XeroAccount.xeroAccounts,
    xeroItems: state.XeroItem.xeroItems,
    xeroTaxRates: state.XeroTaxRate.xeroTaxRates,
    xeroContactsLoading: state.XeroContact.loading,
    xeroAccountsLoading: state.XeroAccount.loading,
    xeroItemsLoading: state.XeroItem.loading,
    xeroTaxRatesLoading: state.XeroTaxRate.loading,
  }));

  const integrationId = linkedIntegration?.integrationId || invoice.integration;
  const fullPath = location.pathname + location.search;
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    dispatch(fetchIntegrations());
  }, []);

  // Fetch Xero data when integration is available
  // Don't update invoice fields here - read initial values from backend only
  useEffect(() => {
    if (linkedIntegration && integrationId) {
      dispatch(fetchXeroContacts(Number(integrationId)));
      dispatch(fetchXeroAccounts(Number(integrationId)));
      dispatch(fetchXeroItems(Number(integrationId)));
      dispatch(fetchXeroTaxRates(Number(integrationId)));
    }
  }, [linkedIntegration, integrationId]);

  useEffect(() => {
    if (invoice?.link) {
      dispatch(setDuplicateError(null));
    }
  }, [invoice?.link]);

  // Normalize docType when Xero integration is linked
  useEffect(() => {
    if (
      linkedIntegration &&
      linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.xero &&
      invoice
    ) {
      // Change payables to bill, receivables to invoice
      if (invoice.docType === "payables") {
        dispatch(
          updateInvoiceField({ invoice, field: "docType", value: "bill" })
        );
      } else if (invoice.docType === "receivables") {
        dispatch(
          updateInvoiceField({ invoice, field: "docType", value: "invoice" })
        );
      }
    }
  }, [linkedIntegration, linkedIntegrationDetails, invoice, dispatch]);

  // Handle field changes for Xero-specific fields
  const handleFieldChange = ({
    values,
    field,
    value,
    label,
  }: {
    values: Invoice2;
    field: string;
    value: any;
    label?: string;
  }) => {
    // Vendor (Contact) name handling
    if (field === "vendorXeroEdited") {
      dispatch(
        updateInvoiceField({
          invoice: values,
          field: "vendorXeroEditedName",
          value: value ? label || getVendorById(value) : "",
        })
      );
    } else if (field === "vendorXeroAutomated") {
      dispatch(
        updateInvoiceField({
          invoice: values,
          field: "vendorXeroAutomatedName",
          value: value ? label || getVendorById(value) : "",
        })
      );
    } else if (field === "vendorXero") {
      dispatch(
        updateInvoiceField({
          invoice: values,
          field: "vendorXeroName",
          value: value ? label || getVendorById(value) : "",
        })
      );
    }
    // Customer (Contact) name handling
    else if (field === "customerXeroEdited") {
      dispatch(
        updateInvoiceField({
          invoice: values,
          field: "customerXeroEditedName",
          value: value ? label || getCustomerById(value) : "",
        })
      );
    } else if (field === "customerXeroAutomated") {
      dispatch(
        updateInvoiceField({
          invoice: values,
          field: "customerXeroAutomatedName",
          value: value ? label || getCustomerById(value) : "",
        })
      );
    } else if (field === "customerXero") {
      dispatch(
        updateInvoiceField({
          invoice: values,
          field: "customerXeroName",
          value: value ? label || getCustomerById(value) : "",
        })
      );
    }
    // Account name handling
    else if (field === "accountXeroEdited") {
      dispatch(
        updateInvoiceField({
          invoice: values,
          field: "accountXeroEditedName",
          value: value ? getAccountById(value) : "",
        })
      );
    } else if (field === "accountXeroAutomated") {
      dispatch(
        updateInvoiceField({
          invoice: values,
          field: "accountXeroAutomatedName",
          value: value ? getAccountById(value) : "",
        })
      );
    } else if (field === "accountXero") {
      dispatch(
        updateInvoiceField({
          invoice: values,
          field: "accountXeroName",
          value: value ? getAccountById(value) : "",
        })
      );
    }

    dispatch(updateInvoiceField({ invoice: values, field, value }));
  };

  // Handle line item changes for Xero-specific fields
  const handleLineItemChange = ({
    values,
    lineIndex,
    field,
    value,
    label,
  }: {
    values: Invoice2;
    lineIndex: number;
    field: string;
    value: any;
    label?: string;
  }) => {
    const updatedValues = { ...values };
    const updatedLines = [...updatedValues.lines];
    const updatedLine = { ...updatedLines[lineIndex] };

    // Category (Account) name handling
    if (field === "categoryXeroEdited") {
      updatedLine.categoryXeroEditedName = value
        ? label || getCategoryById(value)
        : "";
    } else if (field === "categoryXeroAutomated") {
      updatedLine.categoryXeroAutomatedName = value
        ? label || getCategoryById(value)
        : "";
    } else if (field === "categoryXero") {
      updatedLine.categoryXeroName = value
        ? label || getCategoryById(value)
        : "";
    }

    // Service (Item) name handling
    else if (field === "serviceXeroEdited") {
      updatedLine.serviceXeroEditedName = value
        ? label || getServiceById(value)
        : "";
    } else if (field === "serviceXeroAutomated") {
      updatedLine.serviceXeroAutomatedName = value
        ? label || getServiceById(value)
        : "";
    } else if (field === "serviceXero") {
      updatedLine.serviceXeroName = value ? label || getServiceById(value) : "";
    }

    // Tax (Tax Rate) name handling
    else if (field === "taxXeroEdited") {
      updatedLine.taxXeroEditedName = value
        ? label || getTaxCodeById(value)
        : "";
    } else if (field === "taxXeroAutomated") {
      updatedLine.taxXeroAutomatedName = value
        ? label || getTaxCodeById(value)
        : "";
    } else if (field === "taxXero") {
      updatedLine.taxXeroName = value ? label || getTaxCodeById(value) : "";
    }

    // Set the original field value
    updatedLine[field] = value;

    updatedLines[lineIndex] = updatedLine;
    updatedValues.lines = updatedLines;

    dispatch(
      updateInvoiceLineItem({
        invoice: updatedValues,
        lineIndex,
        field,
        value: value || "",
      })
    );
  };

  // Fetch approval status
  useEffect(() => {
    if (invoice?.id && tl.id) {
      dispatch(fetchApprovalStatus(tl.id));
    }

    return () => {
      dispatch(resetState());
    };
  }, [JSON.stringify(invoice), tl.id, dispatch]);

  const handleApprove = async (comment = "") => {
    try {
      await dispatch(approveInvoice(Number(tl.id), comment));
    } catch (error) {
      console.error("Error approving invoice:", error);
    }
  };

  const handleReject = async (comment = "") => {
    try {
      await dispatch(rejectInvoice(Number(tl.id), comment));
    } catch (error) {
      console.error("Error rejecting invoice:", error);
    }
  };

  if (!invoice) return null;

  // Helper functions for Xero data lookups
  const getCategoryById = (id: any) => {
    const category = xeroAccounts.find((x) => x.value === id);
    return category ? formatAccountLabel(category) : "";
  };

  const getServiceById = (id: any) => {
    const service = xeroItems.find((x) => x.value === id);
    return service ? formatItemLabel(service) : "";
  };

  const getTaxCodeById = (id: any) => {
    const taxCode = xeroTaxRates.find((x) => x.value === id);
    return taxCode ? taxCode.name : "";
  };

  const getCustomerById = (id: any) => {
    const customer = xeroContacts.find((x) => x.value === id);
    return customer ? customer.name : "";
  };

  const getVendorById = (id: any) => {
    // Xero uses contacts for both customers and vendors
    const vendor = xeroContacts.find((x) => x.value === id);
    return vendor ? vendor.name : "";
  };

  // Load options functions for async search
  const loadContactOptions = async (inputValue: string) => {
    if (!integrationId) return [];
    try {
      const results = await searchXeroContacts(
        Number(integrationId),
        inputValue
      );
      return results.map((c: any) => ({
        label: c.name || c.Name,
        value: c.contactID || c.ContactID,
      }));
    } catch (error) {
      console.error("Error searching Xero contacts:", error);
      return [];
    }
  };

  // Helper function to format account label with code and name
  const formatAccountLabel = (account: { name: string; code?: string }) => {
    if (account.code) {
      return `${account.code} - ${account.name}`;
    }
    return account.name;
  };

  // Helper function to format item label with code and name
  const formatItemLabel = (item: { name: string; code?: string }) => {
    if (item.code) {
      return `${item.code} - ${item.name}`;
    }
    return item.name;
  };

  // Helper function to format account type for display
  const formatAccountType = (type: string) => {
    // Convert uppercase type to title case (e.g., "REVENUE" -> "Revenue")
    return type
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper function to group accounts by type
  const groupAccountsByType = (accounts: typeof xeroAccounts) => {
    const grouped: { [key: string]: typeof xeroAccounts } = {};

    accounts.forEach((account) => {
      const type = account.type || "OTHER";
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(account);
    });

    // Convert to react-select grouped format
    return Object.keys(grouped)
      .sort()
      .map((type) => ({
        label: formatAccountType(type),
        options: grouped[type].map((account) => ({
          label: formatAccountLabel(account),
          value: account.value,
        })),
      }));
  };

  const loadAccountOptions = async (inputValue: string) => {
    if (!integrationId) return [];
    try {
      const results = await searchXeroAccounts(
        Number(integrationId),
        inputValue
      );
      return results.map((a: any) => ({
        label: formatAccountLabel({
          name: a.name || a.Name,
          code: a.code || a.Code,
        }),
        value: a.accountID || a.AccountID,
      }));
    } catch (error) {
      console.error("Error searching Xero accounts:", error);
      return [];
    }
  };

  const loadItemOptions = async (inputValue: string) => {
    if (!integrationId) return [];
    try {
      const results = await searchXeroItems(Number(integrationId), inputValue);
      return results.map((i: any) => ({
        label: formatItemLabel({
          name: i.name || i.Name,
          code: i.code || i.Code,
        }),
        value: i.itemID || i.ItemID,
      }));
    } catch (error) {
      console.error("Error searching Xero items:", error);
      return [];
    }
  };

  const getAccountById = (id: any) => {
    const account = xeroAccounts.find((x) => x.value === id);
    return account ? formatAccountLabel(account) : "";
  };

  // Xero terminology: invoice = Sales Invoice (ACCREC), bill = Bill (ACCPAY)
  const ctaLabel = `Import ${
    invoice?.docType === "invoice" ? "sales invoice" : "bill"
  } into Xero`;

  // Validation schema for Xero
  const validationSchema = Yup.object().shape({
    ...createBaseValidationSchema(invoice.docType),
    lines: createXeroLineItemsValidation(invoice.docType),
    ...(invoice.docType === "invoice" && {
      customerXero: createXeroCustomerValidation(),
    }),
    // Xero only has "bill" (ACCPAY), no separate "expense" type
    ...(["bill"].includes(invoice.docType) && {
      vendorXero: createXeroVendorValidation(),
    }),
    // Note: Xero doesn't have a separate "expense" type, so no account validation needed
  });

  // Initial values with Xero-specific fields
  const initialValues = {
    ...invoice,
    // Xero customer fields
    customerXero: invoice.customerXero || "",
    customerXeroName: invoice.customerXeroName || "",
    customerXeroEdited: invoice.customerXeroEdited || "",
    customerXeroEditedName: invoice.customerXeroEditedName || "",
    customerXeroAutomated: invoice.customerXeroAutomated || "",
    customerXeroAutomatedName: invoice.customerXeroAutomatedName || "",
    // Xero vendor fields
    vendorXero: invoice.vendorXero || "",
    vendorXeroName: invoice.vendorXeroName || "",
    vendorXeroEdited: invoice.vendorXeroEdited || "",
    vendorXeroEditedName: invoice.vendorXeroEditedName || "",
    vendorXeroAutomated: invoice.vendorXeroAutomated || "",
    vendorXeroAutomatedName: invoice.vendorXeroAutomatedName || "",
    // Xero account fields
    accountXero: invoice.accountXero || "",
    accountXeroName: invoice.accountXeroName || "",
    accountXeroEdited: invoice.accountXeroEdited || "",
    accountXeroEditedName: invoice.accountXeroEditedName || "",
    accountXeroAutomated: invoice.accountXeroAutomated || "",
    accountXeroAutomatedName: invoice.accountXeroAutomatedName || "",
    // Line items with Xero-specific fields
    lines: (invoice.lines || []).map((l: LineItem2) => ({
      ...l,
      categoryXeroEdited: l?.categoryXeroEdited || "",
      serviceXeroEdited: l?.serviceXeroEdited || "",
      categoryXeroAutomated: l?.categoryXeroAutomated || "",
      serviceXeroAutomated: l?.serviceXeroAutomated || "",
      taxXeroEdited: l?.taxXeroEdited || "",
      taxXeroAutomated: l?.taxXeroAutomated || "",
    })),
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        context={{ docType: invoice.docType }}
        onSubmit={(values: Invoice2) => {
          dispatch(handleImportToQbo(values));
        }}
      >
        {({ values, setFieldValue, submitForm, errors, validateForm }) => {
          const integrationWithError =
            linkedIntegrationDetails &&
            (linkedIntegrationDetails.errorMessage ||
              linkedIntegrationDetails.errorCode !== null);

          return (
            <>
              {/* Duplicate error display */}
              <Row>
                <Col className="d-flex justify-content-end align-items-center">
                  <div>
                    {duplicateError && (
                      <Alert
                        variant="danger"
                        className="mb-0 mr-3"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <span>
                          Duplicate {duplicateError.docType} found. View in Xero{" "}
                          <a
                            href={duplicateError.qboUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            here
                          </a>
                        </span>
                        <Button
                          variant="danger"
                          size="sm"
                          className="ml-3"
                          onClick={() =>
                            dispatch(handleImportToQbo(values, true))
                          }
                        >
                          Force Import
                        </Button>
                      </Alert>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Customer (Contact) field for sales invoices - Xero uses ACCREC */}
              <Row>
                {["invoice"].includes(invoice.docType) && (
                  <Col>
                    <FormGroup>
                      <strong>
                        <Form.Label>Customer</Form.Label>
                      </strong>
                      <Field
                        name="customerXero"
                        placeholder="Search or create a new customer"
                        value={
                          invoice.customerXeroEdited ||
                          invoice.customerXeroAutomated ||
                          invoice.customerXero
                            ? {
                                label: getCustomerById(
                                  invoice.customerXeroEdited ||
                                    invoice.customerXeroAutomated ||
                                    invoice.customerXero
                                ),
                                value:
                                  invoice.customerXeroEdited ||
                                  invoice.customerXeroAutomated ||
                                  invoice.customerXero,
                              }
                            : null
                        }
                        component={FormikQboSelect}
                        options={xeroContacts.map((c) => ({
                          label: c.name,
                          value: c.value,
                        }))}
                        loadOptions={loadContactOptions}
                        createFn={async (value: string) => {
                          try {
                            return await dispatch(
                              createAndAddXeroContact(
                                Number(integrationId),
                                value
                              )
                            );
                          } catch (err) {
                            dispatch(
                              createAlert({
                                message:
                                  (err as any)?.message ||
                                  "Failed to create contact. Please try again.",
                                type: "error",
                                timeout: 5000,
                              })
                            );
                            throw err;
                          }
                        }}
                        type="customer"
                        onChangeFn={(value: any, label?: string) => {
                          handleFieldChange({
                            values,
                            field: "customerXeroEdited",
                            value,
                            label,
                          });
                        }}
                      />
                    </FormGroup>
                  </Col>
                )}

                {/* Supplier (Contact) field for bills - Xero uses ACCPAY */}
                {["bill"].includes(invoice.docType) && (
                  <Col>
                    <FormGroup>
                      <strong>
                        <Form.Label>Supplier</Form.Label>
                      </strong>
                      <Field
                        name="vendorXero"
                        placeholder="Search or create a new supplier"
                        value={
                          invoice.vendorXeroEdited ||
                          invoice.vendorXeroAutomated ||
                          invoice.vendorXero
                            ? {
                                label: getVendorById(
                                  invoice.vendorXeroEdited ||
                                    invoice.vendorXeroAutomated ||
                                    invoice.vendorXero
                                ),
                                value:
                                  invoice.vendorXeroEdited ||
                                  invoice.vendorXeroAutomated ||
                                  invoice.vendorXero,
                              }
                            : null
                        }
                        component={FormikQboSelect}
                        options={xeroContacts.map((c) => ({
                          label: c.name,
                          value: c.value,
                        }))}
                        loadOptions={loadContactOptions}
                        createFn={async (value: string) => {
                          try {
                            return await dispatch(
                              createAndAddXeroContact(
                                Number(integrationId),
                                value
                              )
                            );
                          } catch (err) {
                            dispatch(
                              createAlert({
                                message:
                                  (err as any)?.message ||
                                  "Failed to create supplier. Please try again.",
                                type: "error",
                                timeout: 5000,
                              })
                            );
                            throw err;
                          }
                        }}
                        type="vendor"
                        onChangeFn={(value: any, label?: string) => {
                          handleFieldChange({
                            values,
                            field: "vendorXeroEdited",
                            value,
                            label,
                          });
                        }}
                      />
                    </FormGroup>
                  </Col>
                )}

                {/* Note: Xero doesn't have a separate "expense" type like QuickBooks.
                    Use "bill" (ACCPAY) for vendor payments in Xero. */}
              </Row>

              {/* ID, Date, Due Date row */}
              <Row>
                <Col>
                  <FormGroup>
                    <strong>
                      <Form.Label>Id #</Form.Label>
                    </strong>
                    <Field
                      placeholder=""
                      name="id"
                      type="text"
                      component={formikInput}
                      onBlur={(event: any) =>
                        handleFieldChange({
                          values,
                          field: "id",
                          value: event.target.value,
                        })
                      }
                    />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup>
                    <strong>
                      <Form.Label>Date</Form.Label>
                    </strong>
                    <Field
                      data-cy={`${invoice.docType}-date`}
                      placeholder=""
                      name="date"
                      type="text"
                      component={formikInput}
                      onBlur={(event: any) =>
                        handleFieldChange({
                          values,
                          field: "date",
                          value: event.target.value,
                        })
                      }
                    />
                  </FormGroup>
                </Col>
                {["bill", "invoice"].includes(invoice.docType) && (
                  <Col>
                    <FormGroup>
                      <strong>
                        <Form.Label>Due date</Form.Label>
                      </strong>
                      <Field
                        placeholder=""
                        name="dueDate"
                        type="text"
                        component={formikInput}
                        onBlur={(event: any) =>
                          handleFieldChange({
                            values,
                            field: "dueDate",
                            value: event.target.value,
                          })
                        }
                      />
                    </FormGroup>
                  </Col>
                )}
              </Row>

              {/* Subtotal / Tax / Total row */}
              {(() => {
                const consistency = checkInvoiceConsistency(values);
                const headerMsg = consistency.headerMismatch
                  ? `Subtotal + Tax (${(
                      consistency.subTotal + consistency.tax
                    ).toFixed(2)}) ≠ Total (${consistency.total.toFixed(
                      2
                    )}). Δ ${formatDelta(consistency.headerDelta)}.`
                  : "";
                return (
                  <Row>
                    <Col>
                      <FormGroup>
                        <strong>
                          <Form.Label>
                            Subtotal
                            <InvoiceFieldWarning
                              id="xero-subtotal"
                              show={consistency.headerMismatch}
                              message={headerMsg}
                            />
                          </Form.Label>
                        </strong>
                        <Field
                          placeholder=""
                          name="subTotal"
                          type="text"
                          component={formikInput}
                          onBlur={(event: any) =>
                            handleFieldChange({
                              values,
                              field: "subTotal",
                              value: event.target.value,
                            })
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <strong>
                          <Form.Label>
                            Tax
                            <InvoiceFieldWarning
                              id="xero-tax"
                              show={consistency.headerMismatch}
                              message={headerMsg}
                            />
                          </Form.Label>
                        </strong>
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <Field
                              placeholder=""
                              name="tax"
                              type="text"
                              component={formikInput}
                              onBlur={(event: any) =>
                                handleFieldChange({
                                  values,
                                  field: "tax",
                                  value: event.target.value,
                                })
                              }
                            />
                          </div>
                          {values.tax &&
                            values.tax !== "" &&
                            Number(values.tax) !== 0 && (
                              <Button
                                variant="link"
                                size="sm"
                                className="ml-2"
                                onClick={() => {
                                  const taxValue = values.tax;
                                  const newLineItem = createTaxLineItem(taxValue);
                                  const updatedInvoice = {
                                    ...values,
                                    lines: [...values.lines, newLineItem],
                                    tax: "",
                                  };

                                  dispatch(
                                    updateInvoiceField({
                                      invoice: updatedInvoice,
                                      field: "lines",
                                      value: updatedInvoice.lines,
                                    })
                                  );
                                  dispatch(
                                    updateInvoiceField({
                                      invoice: updatedInvoice,
                                      field: "tax",
                                      value: "",
                                    })
                                  );
                                  setFieldValue("lines", updatedInvoice.lines);
                                  setFieldValue("tax", "");
                                }}
                                title="Add tax as line item"
                              >
                                <FontAwesomeIcon icon="plus" className="mr-1" />
                                Add Tax as Line Item
                              </Button>
                            )}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <strong>
                          <Form.Label>
                            Total
                            <InvoiceFieldWarning
                              id="xero-total"
                              show={consistency.headerMismatch}
                              message={headerMsg}
                            />
                          </Form.Label>
                        </strong>
                        <Field
                          placeholder=""
                          name="total"
                          type="text"
                          component={formikInput}
                          onBlur={(event: any) =>
                            handleFieldChange({
                              values,
                              field: "total",
                              value: event.target.value,
                            })
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                );
              })()}

              {/* Line Items */}
              <Row className="ml-0">
                <FieldArray name="lines" key={invoice?.integration}>
                  {({ remove, push }) => (
                    <div
                      className="w-100"
                      style={{
                        maxHeight: "120vh",
                        overflowY: "scroll",
                      }}
                    >
                      {values.lines.length > 0 &&
                        values.lines.map((lineItem: any, index: number) => (
                          <div className="row my-2" key={`line-${index}`}>
                            {/* Item/Account column - show both for invoices and bills */}
                            <div className="col-3 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label>Item / Account</label>
                                </strong>
                              )}

                              {/* Item (Product/Service) - show for both invoices and bills */}
                              <div
                                style={{
                                  position: "relative",
                                  marginBottom: "1rem",
                                }}
                              >
                                <CategorizationBadge
                                  primaryCondition={lineItem.serviceXeroEdited}
                                  secondaryCondition={
                                    lineItem.serviceXeroAutomated
                                  }
                                />
                                <Field
                                  key={`lines.${index}.service-${lineItem.serviceXeroEdited}-${lineItem.serviceXeroAutomated}`}
                                  name={`lines.${index}.serviceXeroEdited`}
                                  placeholder="Item (Product/Service)"
                                  value={{
                                    label:
                                      lineItem?.serviceXeroEditedName ||
                                      getServiceById(
                                        lineItem?.serviceXeroEdited ||
                                          lineItem?.serviceXeroAutomated ||
                                          lineItem?.serviceXero ||
                                          ""
                                      ),
                                    value:
                                      lineItem?.serviceXeroEdited ||
                                      lineItem?.serviceXeroAutomated ||
                                      lineItem?.serviceXero ||
                                      "",
                                  }}
                                  className="pr-0"
                                  component={FormikQboSelect}
                                  options={xeroItems.map((item) => ({
                                    label: formatItemLabel(item),
                                    value: item.value,
                                  }))}
                                  loadOptions={loadItemOptions}
                                  createFn={async (value: string) => {
                                    try {
                                      return await dispatch(
                                        createAndAddXeroItem(
                                          Number(integrationId),
                                          value
                                        )
                                      );
                                    } catch (err) {
                                      dispatch(
                                        createAlert({
                                          message:
                                            (err as any)?.message ||
                                            "Failed to create item. Please try again.",
                                          type: "error",
                                          timeout: 5000,
                                        })
                                      );
                                      throw err;
                                    }
                                  }}
                                  onChangeFn={(value: any, label?: string) => {
                                    handleLineItemChange({
                                      values,
                                      lineIndex: index,
                                      field: "serviceXeroEdited",
                                      value: value || "",
                                      label,
                                    });
                                  }}
                                  type="service"
                                  style={{
                                    marginTop:
                                      index === 0 &&
                                      (lineItem.serviceXeroEdited ||
                                        lineItem.serviceXeroAutomated)
                                        ? "1rem"
                                        : "0",
                                  }}
                                />
                              </div>

                              {/* Account (Category) - show for both invoices and bills */}
                              <div
                                style={{
                                  position: "relative",
                                  marginBottom: "1rem",
                                }}
                              >
                                <CategorizationBadge
                                  primaryCondition={lineItem.categoryXeroEdited}
                                  secondaryCondition={
                                    lineItem.categoryXeroAutomated
                                  }
                                />
                                <Field
                                  key={`lines.${index}.category-${lineItem.categoryXeroEdited}-${lineItem.categoryXeroAutomated}`}
                                  name={`lines.${index}.categoryXeroEdited`}
                                  placeholder="Account"
                                  value={{
                                    label: getCategoryById(
                                      lineItem?.categoryXeroEdited ||
                                        lineItem?.categoryXeroAutomated ||
                                        ""
                                    ),
                                    value:
                                      lineItem?.categoryXeroEdited ||
                                      lineItem?.categoryXeroAutomated ||
                                      "",
                                  }}
                                  component={FormikQboSelect}
                                  options={groupAccountsByType(xeroAccounts)}
                                  loadOptions={loadAccountOptions}
                                  createFn={async (value: string) => {
                                    try {
                                      return await dispatch(
                                        createAndAddXeroAccount(
                                          Number(integrationId),
                                          value
                                        )
                                      );
                                    } catch (err) {
                                      dispatch(
                                        createAlert({
                                          message:
                                            (err as any)?.message ||
                                            "Failed to create account. Please try again.",
                                          type: "error",
                                          timeout: 5000,
                                        })
                                      );
                                      throw err;
                                    }
                                  }}
                                  onChangeFn={(value: any, label?: string) => {
                                    handleLineItemChange({
                                      values,
                                      lineIndex: index,
                                      field: "categoryXeroEdited",
                                      value: !value ? "" : value,
                                      label,
                                    });
                                  }}
                                  type="category"
                                  style={{
                                    marginTop:
                                      index === 0 &&
                                      (lineItem.categoryXeroEdited ||
                                        lineItem.categoryXeroAutomated)
                                        ? "1rem"
                                        : "0",
                                  }}
                                />
                              </div>
                            </div>

                            {/* Description column */}
                            <div className="col-3 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label htmlFor={`lines.${index}.item`}>
                                    Description
                                  </label>
                                </strong>
                              )}
                              <Field
                                name={`lines.${index}.item`}
                                placeholder=""
                                type="text"
                                component={formikArrayInput}
                                onBlur={(event: any) =>
                                  handleLineItemChange({
                                    values,
                                    lineIndex: index,
                                    field: "item",
                                    value: event.target.value,
                                  })
                                }
                                style={{
                                  marginTop: getMarginTop(
                                    index,
                                    lineItem,
                                    "1rem",
                                    "0"
                                  ),
                                }}
                              />
                            </div>

                            {/* Product Code column */}
                            <div className="col-1 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label
                                    htmlFor={`lines.${index}.productCode`}
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "100%",
                                      display: "block",
                                    }}
                                  >
                                    Product Code
                                  </label>
                                </strong>
                              )}
                              <Field
                                name={`lines.${index}.productCode`}
                                value={
                                  lineItem.productCode ||
                                  (lineItem.extraFields &&
                                    lineItem.extraFields.PRODUCT_CODE) ||
                                  ""
                                }
                                placeholder=""
                                type="text"
                                component={formikArrayInput}
                                onBlur={(event: any) =>
                                  handleLineItemChange({
                                    values,
                                    lineIndex: index,
                                    field: "productCode",
                                    value: event.target.value,
                                  })
                                }
                                style={{
                                  marginTop: getMarginTop(
                                    index,
                                    lineItem,
                                    "1rem",
                                    "0"
                                  ),
                                }}
                              />
                            </div>

                            {/* Quantity column */}
                            <div className="col-1 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "100%",
                                      display: "block",
                                    }}
                                    htmlFor={`lines.${index}.quantity`}
                                  >
                                    Quantity
                                  </label>
                                </strong>
                              )}
                              <Field
                                name={`lines.${index}.quantity`}
                                placeholder=""
                                type="text"
                                component={formikArrayInput}
                                onBlur={(event: any) =>
                                  handleLineItemChange({
                                    values,
                                    lineIndex: index,
                                    field: "quantity",
                                    value: event.target.value,
                                  })
                                }
                                style={{
                                  marginTop: getMarginTop(
                                    index,
                                    lineItem,
                                    "1rem",
                                    "0"
                                  ),
                                }}
                              />
                            </div>

                            {/* Unit Price column */}
                            <div className="col-1 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label
                                    htmlFor={`lines.${index}.unitPrice`}
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "100%",
                                      display: "block",
                                    }}
                                  >
                                    Unit Price
                                  </label>
                                </strong>
                              )}
                              <Field
                                name={`lines.${index}.unitPrice`}
                                placeholder=""
                                type="text"
                                component={formikArrayInput}
                                onBlur={(event: any) =>
                                  handleLineItemChange({
                                    values,
                                    lineIndex: index,
                                    field: "unitPrice",
                                    value: event.target.value,
                                  })
                                }
                                style={{
                                  marginTop: getMarginTop(
                                    index,
                                    lineItem,
                                    "1rem",
                                    "0"
                                  ),
                                }}
                              />
                            </div>

                            {/* Price column */}
                            <div className="col-1 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label htmlFor={`lines.${index}.price`}>
                                    Price
                                  </label>
                                </strong>
                              )}
                              {(() => {
                                const lc = checkInvoiceConsistency(values);
                                const msg = lc.linesMismatch
                                  ? `Line items sum (${lc.linesSum.toFixed(
                                      2
                                    )}) ≠ Subtotal (${lc.subTotal.toFixed(
                                      2
                                    )}). Δ ${formatDelta(lc.linesDelta)}.`
                                  : "";
                                return (
                                  <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                      <Field
                                        name={`lines.${index}.price`}
                                        placeholder=""
                                        type="text"
                                        component={formikArrayInput}
                                        onBlur={(event: any) =>
                                          handleLineItemChange({
                                            values,
                                            lineIndex: index,
                                            field: "price",
                                            value: event.target.value,
                                          })
                                        }
                                        style={{
                                          marginTop: getMarginTop(
                                            index,
                                            lineItem,
                                            "1rem",
                                            "0"
                                          ),
                                        }}
                                      />
                                    </div>
                                    <InvoiceFieldWarning
                                      id={`xero-line-price-${index}`}
                                      show={lc.linesMismatch}
                                      message={msg}
                                    />
                                  </div>
                                );
                              })()}
                            </div>

                            {/* Tax column */}
                            <div className="col-1 pr-0">
                              {index === 0 && (
                                <strong>
                                  <label htmlFor={`lines.${index}.taxXero`}>
                                    Tax
                                  </label>
                                </strong>
                              )}
                              <div
                                style={{
                                  position: "relative",
                                  marginBottom: "1rem",
                                  marginTop: getMarginTop(
                                    index,
                                    lineItem,
                                    "1rem",
                                    "0"
                                  ),
                                }}
                              >
                                <CategorizationBadge
                                  primaryCondition={lineItem.taxXeroEdited}
                                  secondaryCondition={lineItem.taxXeroAutomated}
                                />
                                <Field
                                  key={`lines.${index}.tax-${lineItem.taxXeroEdited}-${lineItem.taxXeroAutomated}`}
                                  name={`lines.${index}.taxXeroEdited`}
                                  placeholder="Tax"
                                  value={{
                                    label: getTaxCodeById(
                                      lineItem?.taxXeroEdited ||
                                        lineItem?.taxXeroAutomated ||
                                        ""
                                    ),
                                    value:
                                      lineItem?.taxXeroEdited ||
                                      lineItem?.taxXeroAutomated ||
                                      "",
                                  }}
                                  className="pr-0"
                                  component={FormikQboSelect}
                                  options={xeroTaxRates.map((c) => ({
                                    label: c.name,
                                    value: c.value,
                                  }))}
                                  onChangeFn={(value: any, label?: string) => {
                                    handleLineItemChange({
                                      values,
                                      lineIndex: index,
                                      field: "taxXeroEdited",
                                      value: value || "",
                                      label,
                                    });
                                  }}
                                  type="tax"
                                />
                              </div>
                            </div>

                            {/* Delete button */}
                            <TrackedButtonWithTooltip
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginRight: "0.5rem",
                                marginTop: getMarginTop(
                                  index,
                                  lineItem,
                                  "1.5rem",
                                  "-1rem"
                                ),
                              }}
                              id={index.toString()}
                              placement="top"
                              variant="link"
                              renderTooltip={() =>
                                values.lines.length === 1 ? (
                                  <>Cannot delete last line item</>
                                ) : (
                                  <>Delete Line Item</>
                                )
                              }
                              action="removeItem"
                              onClick={() =>
                                values.lines.length > 1 && remove(index)
                              }
                              disabled={values.lines.length === 1}
                            >
                              <FontAwesomeIcon icon="trash" />
                            </TrackedButtonWithTooltip>
                          </div>
                        ))}

                      <Button
                        variant="link"
                        className="secondary"
                        onClick={() => push(defaultInvoiceLine)}
                      >
                        <FontAwesomeIcon icon="plus" />
                        <span className="ml-1">Add Line Item</span>
                      </Button>
                      <div className="p-5"></div>
                    </div>
                  )}
                </FieldArray>
              </Row>

              {/* Bottom duplicate error */}
              <Row className="mt-2 text-right">
                <Col
                  md={12}
                  className="d-flex justify-content-end align-items-center"
                >
                  {duplicateError && (
                    <Alert
                      variant="danger"
                      className="mb-0 mr-3"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <span>
                        Duplicate {duplicateError.docType} found. View in Xero{" "}
                        <a
                          href={duplicateError.qboUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          here
                        </a>
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        className="ml-3"
                        onClick={() =>
                          dispatch(handleImportToQbo(values, true))
                        }
                      >
                        Force Import
                      </Button>
                    </Alert>
                  )}
                </Col>
              </Row>
            </>
          );
        }}
      </Formik>

      <TagRegexesInvoice
        categories={
          ["bill"].includes(invoice.docType) ? xeroAccounts : xeroItems
        }
        docType={invoice.docType}
        integrationId={integrationId ? Number(integrationId) : null}
      />

      <ApprovalHistory
        show={showHistory}
        onHide={() => setShowHistory(false)}
        transactionLineId={tl.id ? Number(tl.id) : 0}
      />
    </>
  );
};

export default InvoiceXero;
